/**
 * Settings → Models: the built-in models (Workers AI, private, always on when
 * the binding exists) and then "Your providers" — one row per `ProviderKey`,
 * in the order `GET /providers` already returns them
 * (worker/routes/providers.ts's `ORDER`). Follows the user's annotation
 * (project/uploads/draw-6e48cb8c…) for the row shape and the prototype
 * (lines ~322-338) for the built-ins card and the warning switch.
 */

import { useCallback, useEffect, useState } from 'react';
import { listProviders, patchSettings, speak, type ProviderKey, type ProviderStatus, type VoiceName } from '../../api';
import { GALLERY_MODELS, VOICES, type ImageModelKey } from '@wire';
import { unlockAudio } from '../../audio/speaker';
import { assistantSettings, hello, inAndroid } from '../../android';
import { inDesktop } from '../../desktop';
import { ds } from '../../ds';
import { ProviderDialog } from '../ProviderDialog';
import { useApp } from '../../state';
import { MonoLabel, Ph } from '../ui';
import { modelIcon } from '../composer/ModelMenu';

const ICONS: Record<ProviderKey, string> = {
  'workers-ai': 'shield-check',
  ollama: 'hard-drives',
  lmstudio: 'desktop-tower',
  anthropic: 'globe-simple',
  openai: 'globe-simple',
  google: 'globe-simple',
  openrouter: 'globe-simple',
  custom: 'plug'
};

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/** The row's status text and its action label — null when the row has none
 *  (Workers AI and LM Studio, the desktop app's). Inside the desktop app LM
 *  Studio is not reachable yet either, so it says so there. */
export function rowText(p: ProviderStatus, desktop: boolean): { status: string; action: string | null } {
  if (p.provider === 'workers-ai') return { status: 'Built in', action: null };
  if (p.provider === 'lmstudio') {
    return { status: desktop ? 'Not available yet' : 'In the desktop app', action: null };
  }

  if (p.provider === 'custom' || p.provider === 'ollama') {
    return p.state === 'connected'
      ? { status: p.baseUrl ? hostOf(p.baseUrl) : 'Connected', action: 'Edit' }
      : { status: p.provider === 'ollama' ? 'Your own server' : 'Any OpenAI-compatible endpoint', action: 'Add' };
  }

  if (p.provider === 'openrouter') {
    return p.state === 'connected'
      ? { status: `Your key · …${p.keyHint}`, action: 'Edit' }
      : { status: 'Not connected', action: 'Connect' };
  }

  // anthropic, openai, google
  if (p.state === 'mauvely') return { status: "Using Mauvely's key", action: 'Add your key' };
  if (p.state === 'connected') return { status: `Your key · …${p.keyHint}`, action: 'Edit' };
  return { status: 'No key', action: 'Add key' };
}

function ProviderRow({ status, onPick }: { status: ProviderStatus; onPick: (s: ProviderStatus) => void }) {
  const { status: statusText, action } = rowText(status, inDesktop());
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 58, padding: '0 16px', borderBottom: '1px solid var(--border-subtle)' }}>
      <span
        style={{
          width: 32,
          height: 32,
          flex: 'none',
          borderRadius: 9,
          background: 'var(--app-panel-sunken)',
          color: 'var(--text-muted)',
          display: 'grid',
          placeItems: 'center'
        }}
      >
        <Ph name={ICONS[status.provider]} size={16} />
      </span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>{status.name}</span>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {statusText}
        </span>
      </div>
      {action ? (
        <button
          type="button"
          className="bare hov-strong"
          onClick={() => onPick(status)}
          style={{ fontSize: 13, color: 'var(--text-link)' }}
        >
          {action}
        </button>
      ) : null}
    </div>
  );
}

export function ModelsTab() {
  const { me, catalogue, patchMe, refreshMe } = useApp();
  const { Switch } = ds();
  const [providers, setProviders] = useState<ProviderStatus[] | null>(null);
  const [dialogFor, setDialogFor] = useState<ProviderStatus | null>(null);

  const load = useCallback(() => {
    listProviders()
      .then((r) => setProviders(r.providers))
      .catch(() => setProviders([]));
  }, []);

  useEffect(() => load(), [load]);

  if (!me) return null;
  const builtinModels = (catalogue?.models ?? []).filter((m) => m.billing === 'mauvely');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <MonoLabel>Built-in models</MonoLabel>
      <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', overflow: 'hidden' }}>
        {builtinModels.map((m) => (
          <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 50, padding: '0 16px', borderBottom: '1px solid var(--border-subtle)' }}>
            <Ph name={modelIcon(m).name} size={16} color={modelIcon(m).color} />
            <span style={{ flex: 1, fontSize: 14, color: 'var(--text-strong)' }}>{m.name}</span>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{m.meta ?? 'Workers AI'}</span>
          </div>
        ))}
      </div>

      <MonoLabel style={{ paddingTop: 8 }}>Your providers</MonoLabel>
      <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', overflow: 'hidden' }}>
        {(providers ?? []).map((p) => (
          <ProviderRow key={p.provider} status={p} onPick={setDialogFor} />
        ))}
      </div>

      <div
        style={{
          background: 'var(--surface-card)',
          borderRadius: 'var(--radius-app-card)',
          padding: '14px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 14
        }}
      >
        <span style={{ flex: 1, fontSize: 14, color: 'var(--text-strong)' }}>Warn before using third-party models</span>
        <Switch
          checked={me.settings.warnThirdParty}
          onChange={(v) => {
            patchSettings({ warnThirdParty: v })
              .then((settings) => patchMe((x) => ({ ...x, settings })))
              .catch(() => void refreshMe());
          }}
        />
      </div>

      <div
        style={{
          background: 'var(--surface-card)',
          borderRadius: 'var(--radius-app-card)',
          padding: '14px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 14
        }}
      >
        <span style={{ flex: 1, fontSize: 14, color: 'var(--text-strong)' }}>Image model for chats and /image</span>
        <select
          aria-label="Image model"
          value={me.settings.imageModel}
          onChange={(e) => {
            const imageModel = e.target.value as ImageModelKey;
            patchMe((x) => ({ ...x, settings: { ...x.settings, imageModel } }));
            patchSettings({ imageModel })
              .then((settings) => patchMe((x) => ({ ...x, settings })))
              .catch(() => void refreshMe());
          }}
          style={{
            font: 'inherit',
            fontSize: 13,
            color: 'var(--text-body)',
            background: 'var(--surface-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-sm)',
            padding: '6px 8px',
            maxWidth: 220
          }}
        >
          {GALLERY_MODELS.map((g) => (
            <option key={g.key} value={g.key}>
              {g.name}
            </option>
          ))}
        </select>
      </div>

      {me.capabilities.voice ? <VoicePicker /> : null}
      {inAndroid() ? <PhoneAssistant /> : null}

      {dialogFor ? (
        <ProviderDialog status={dialogFor} onClose={() => setDialogFor(null)} onSaved={load} />
      ) : null}
    </div>
  );
}

/** In the Android app: whether Assist is the phone's assistant, and the way
 *  to Android's setting for it, which only Android may change. */
function PhoneAssistant() {
  const { Button } = ds();
  const [held, setHeld] = useState<boolean | null>(null);
  useEffect(() => {
    const check = () => void hello().then((h) => setHeld(h ? h.assistantRole : null));
    check();
    // Back from Android's settings.
    document.addEventListener('visibilitychange', check);
    return () => document.removeEventListener('visibilitychange', check);
  }, []);
  if (held === null) return null;
  return (
    <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
      <span style={{ flex: 1, fontSize: 14, color: 'var(--text-strong)' }}>
        {held ? 'Assist is your phone’s assistant' : 'Use Assist as your phone’s assistant'}
      </span>
      <Button variant="ghost" density="app" size="sm" onClick={() => void assistantSettings()}>
        Change
      </Button>
    </div>
  );
}

const SAMPLE = 'Hello. This is how I sound in voice mode.';

/** The voice voice mode speaks in, each one heard before it is picked. */
function VoicePicker() {
  const { me, patchMe, refreshMe } = useApp();
  const [playing, setPlaying] = useState<VoiceName | null>(null);
  if (!me) return null;
  const chosen = me.settings.voice;

  const choose = (voice: VoiceName) => {
    patchMe((x) => ({ ...x, settings: { ...x.settings, voice } }));
    patchSettings({ voice })
      .then((settings) => patchMe((x) => ({ ...x, settings })))
      .catch(() => void refreshMe());
  };

  const play = (voice: VoiceName) => {
    // Started inside the click, so it may play when the words arrive.
    const ctx = unlockAudio();
    setPlaying(voice);
    speak(SAMPLE, undefined, voice)
      .then((wav) => ctx.decodeAudioData(wav))
      .then(
        (buffer) =>
          new Promise<void>((resolve) => {
            const source = ctx.createBufferSource();
            source.buffer = buffer;
            source.connect(ctx.destination);
            source.onended = () => resolve();
            source.start();
          })
      )
      .catch(() => undefined)
      .finally(() => {
        void ctx.close().catch(() => undefined);
        setPlaying((p) => (p === voice ? null : p));
      });
  };

  return (
    <>
      <MonoLabel style={{ paddingTop: 8 }}>Voice</MonoLabel>
      <div role="radiogroup" aria-label="Voice" style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', overflow: 'hidden' }}>
        {VOICES.map((v) => (
          <div key={v} style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 48, padding: '0 8px 0 16px', borderBottom: '1px solid var(--border-subtle)' }}>
            <button
              type="button"
              role="radio"
              aria-checked={v === chosen}
              className="bare"
              onClick={() => choose(v)}
              style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, fontSize: 14, color: 'var(--text-strong)', textAlign: 'left' }}
            >
              <Ph name={v === chosen ? 'radio-button' : 'circle'} fill={v === chosen} size={17} color={v === chosen ? 'var(--primary)' : 'var(--text-faint)'} />
              {v[0]!.toUpperCase() + v.slice(1)}
            </button>
            <button
              type="button"
              className="bare hov-tint"
              aria-label={`Hear ${v[0]!.toUpperCase() + v.slice(1)}`}
              disabled={playing !== null}
              onClick={() => play(v)}
              style={{ width: 40, height: 40, display: 'grid', placeItems: 'center', borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)' }}
            >
              <Ph name={playing === v ? 'circle-notch' : 'play'} className={playing === v ? 'spin' : undefined} size={16} />
            </button>
          </div>
        ))}
      </div>
    </>
  );
}
